import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import topbarImage from "../assets/Windroos-topbar.png";
import { getReleaseLabel, getReleaseMetaLine, releaseInfo } from "../config/releaseInfo";
import { COLORS } from "../theme/tokens";

const NAV_LINKS = [
  { href: "/#in-de-klas", label: "In de klas" },
  { href: "/#freinet", label: "Freinet" },
  { href: "/#school", label: "Onze school" },
  { href: "/#nieuws", label: "Nieuws" },
];

const PARENT_UTILITY_LINKS = [
  { href: "/kalender", label: "Kalender" },
  { href: "/documenten", label: "Documenten" },
  { href: "/contact", label: "Contact" },
  { href: "/oudervereniging", label: "Ouderparticipatie" },
];

const STICHT_SCHOOL_LINKS = [
  { href: "https://www.aloysius-school.nl/", label: "Aloysius school", external: true },
  { href: "https://www.daltonnicolaas.net/", label: "Daltonschool Nicolaas", external: true },
  { href: "http://www.damiaanschool.nl/", label: "Damiaanschool", external: true },
  { href: "http://www.bsdegriffel.nl/", label: "KBS De Griffel", external: true },
  { href: "http://www.montinischool.nl/", label: "Montini Jenaplanschool", external: true },
  { href: "http://www.valkenheuvel.nl/", label: "Valkenheuvel", external: true },
  { href: "https://www.kameleondendolder.nl/", label: "De Kameleon", external: true },
];

const FOOTER_COLUMNS = [
  {
    title: "De Windroos",
    body: "Vernieuwingsschool in Wijk bij Duurstede, onderdeel van Het Sticht.",
  },
  {
    title: "Snelle routes",
    links: [
      { href: "/kalender", label: "Kalender" },
      { href: "/contact", label: "Contact" },
      { href: "/documenten", label: "Documenten" },
      { href: "/nieuw-schoolgebouw", label: "Nieuw schoolgebouw" },
    ],
  },
  {
    title: "Het Sticht",
    body: "De Windroos is onderdeel van Stichting Openbaar Primair Onderwijs Het Sticht.",
    links: [
      { href: "https://www.hetsticht.nl/", label: "hetsticht.nl", external: true },
    ],
  },
  {
    title: "Andere Sticht-scholen",
    links: STICHT_SCHOOL_LINKS,
  },
];

function TopRibbon() {
  return (
    <Box
      aria-hidden="true"
      sx={{
        backgroundColor: COLORS.lightBlue,
        borderBottom: `1px solid ${COLORS.line}`,
        overflow: "hidden",
      }}
    >
      <Box
        component="img"
        src={topbarImage}
        alt=""
        sx={{
          display: "block",
          width: "100%",
          height: "auto",
        }}
      />
    </Box>
  );
}

function NavBar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileMenuButtonRef = useRef(null);

  useEffect(() => {
    if (!mobileOpen) return undefined;

    function closeOnEscape(event) {
      if (event.key !== "Escape") return;
      setMobileOpen(false);
      mobileMenuButtonRef.current?.focus();
    }

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileOpen]);

  return (
    <Box
      component="nav"
      aria-label="Hoofdmenu"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        fontFamily: "Poppins, sans-serif",
        backgroundColor: "rgba(255, 255, 255, 0.93)",
        borderBottom: `1px solid ${COLORS.line}`,
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        px: "clamp(18px, 4vw, 56px)",
        py: "10px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          maxWidth: 1240,
          mx: "auto",
        }}
      >
        <Box
          component="a"
          href="/#welcome"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
            textDecoration: "none",
            color: COLORS.red,
            fontWeight: 400,
            flexShrink: 0,
          }}
          aria-label="De Windroos — terug naar boven"
        >
          <Box
            aria-hidden="true"
            sx={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              backgroundColor: COLORS.orange,
              color: "#fff",
              display: "inline-grid",
              placeItems: "center",
              fontFamily: "windroos, Georgia, serif",
              fontWeight: 400,
              fontSize: "1.05rem",
              lineHeight: 1,
              boxShadow: "0 8px 20px rgba(239, 125, 0, 0.2)",
            }}
          >
            W
          </Box>
          <Box
            component="span"
            sx={{
              fontFamily: "windroos, Georgia, serif",
              fontSize: "clamp(25px, 2.4vw, 34px)",
              lineHeight: 1,
            }}
          >
            Windroos
          </Box>
        </Box>

        <Box
          sx={{
            display: { xs: "none", lg: "flex" },
            alignItems: "center",
            gap: 1,
            flexWrap: "nowrap",
          }}
        >
          {NAV_LINKS.map((link) => (
            <Box
              key={link.href}
              component="a"
              href={link.href}
              sx={{
                minHeight: 42,
                display: "inline-flex",
                alignItems: "center",
                padding: "9px 14px",
                boxSizing: "border-box",
                borderRadius: 1,
                textDecoration: "none",
                fontWeight: 600,
                color: COLORS.ink,
                "&:hover": { backgroundColor: "rgba(60, 143, 153, 0.08)" },
              }}
            >
              {link.label}
            </Box>
          ))}
          <Box
            component="details"
            sx={{
              position: "relative",
              "&[open] > summary::after": { transform: "rotate(180deg)" },
            }}
          >
            <Box
              component="summary"
              sx={{
                minHeight: 42,
                px: 1.5,
                borderRadius: 1,
                color: COLORS.blueInk,
                border: "1px solid rgba(60, 143, 153, 0.34)",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                cursor: "pointer",
                fontWeight: 700,
                listStyle: "none",
                "&::-webkit-details-marker": { display: "none" },
                "&::after": {
                  content: '"▾"',
                  fontSize: "0.85rem",
                  transition: "transform 120ms ease",
                },
                "&:hover": { backgroundColor: "rgba(60, 143, 153, 0.08)" },
              }}
            >
              Voor ouders
            </Box>
            <Box
              aria-label="Voor ouders"
              sx={{
                position: "absolute",
                zIndex: 30,
                top: "calc(100% + 8px)",
                right: 0,
                minWidth: 210,
                p: 0.75,
                borderRadius: 1,
                backgroundColor: "#fff",
                border: `1px solid ${COLORS.line}`,
                boxShadow: "0 16px 36px rgba(32, 48, 51, 0.14)",
              }}
            >
              {PARENT_UTILITY_LINKS.map((link) => (
                <Box
                  key={link.href}
                  component="a"
                  href={link.href}
                  sx={{
                    minHeight: 44,
                    px: 1.5,
                    display: "flex",
                    alignItems: "center",
                    borderRadius: 1,
                    color: COLORS.ink,
                    textDecoration: "none",
                    fontWeight: 600,
                    "&:hover": { backgroundColor: "rgba(60, 143, 153, 0.08)" },
                  }}
                >
                  {link.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Button
            href="/aanmelden"
            disableElevation
            sx={{
              m: 0,
              minHeight: 42,
              px: 2,
              borderRadius: 1,
              fontFamily: "Poppins, sans-serif",
              fontSize: "1rem",
              fontWeight: 700,
              textTransform: "none",
              backgroundColor: COLORS.orange,
              color: `${COLORS.ink} !important`,
              boxShadow: "0 10px 20px rgba(239, 125, 0, 0.22)",
              "&:hover": { backgroundColor: "#E87800" },
            }}
          >
            Kennismaken
          </Button>
        </Box>

        <IconButton
          ref={mobileMenuButtonRef}
          aria-label={mobileOpen ? "Menu sluiten" : "Menu openen"}
          aria-expanded={mobileOpen}
          aria-controls={mobileOpen ? "mobile-navigation" : undefined}
          onClick={() => setMobileOpen((prev) => !prev)}
          sx={{ display: { xs: "inline-flex", lg: "none" }, color: COLORS.ink }}
        >
          {mobileOpen ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      {mobileOpen && (
        <Box
          id="mobile-navigation"
          sx={{
            display: { xs: "grid", lg: "none" },
            gap: 0.5,
            mt: 1.5,
            pb: 1.5,
            borderTop: `1px solid ${COLORS.line}`,
            pt: 1.5,
          }}
        >
          <Box
            component="span"
            sx={{
              px: 1,
              pb: 0.5,
              color: COLORS.muted,
              fontSize: "0.72rem",
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Ontdek de school
          </Box>
          {NAV_LINKS.map((link) => (
            <Box
              key={link.href}
              component="a"
              href={link.href}
              onClick={() => setMobileOpen(false)}
              sx={{
                minHeight: 42,
                display: "flex",
                alignItems: "center",
                padding: "10px 8px",
                textDecoration: "none",
                fontWeight: 600,
                color: COLORS.ink,
                borderRadius: 1,
                "&:hover": { backgroundColor: "rgba(60, 143, 153, 0.08)" },
              }}
            >
              {link.label}
            </Box>
          ))}
          <Box
            component="span"
            sx={{
              mt: 1,
              px: 1,
              pt: 1.5,
              pb: 0.5,
              borderTop: `1px solid ${COLORS.line}`,
              color: COLORS.muted,
              fontSize: "0.72rem",
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Voor ouders
          </Box>
          {PARENT_UTILITY_LINKS.map((link) => (
            <Box
              key={link.href}
              component="a"
              href={link.href}
              onClick={() => setMobileOpen(false)}
              sx={{
                minHeight: 42,
                display: "flex",
                alignItems: "center",
                padding: "10px 8px",
                textDecoration: "none",
                fontWeight: 600,
                color: COLORS.blueInk,
                borderRadius: 1,
                "&:hover": { backgroundColor: "rgba(60, 143, 153, 0.08)" },
              }}
            >
              {link.label}
            </Box>
          ))}
          <Button
            href="/aanmelden"
            onClick={() => setMobileOpen(false)}
            disableElevation
            sx={{
              m: 0,
              mt: 1,
              minHeight: 42,
              borderRadius: 1,
              fontFamily: "Poppins, sans-serif",
              fontSize: "1rem",
              fontWeight: 700,
              textTransform: "none",
              backgroundColor: COLORS.orange,
              color: `${COLORS.ink} !important`,
              "&:hover": { backgroundColor: "#E87800" },
            }}
          >
            Kennismaken
          </Button>
        </Box>
      )}
    </Box>
  );
}

function ShellFooter() {
  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: "#fff",
        px: "clamp(20px, 5vw, 72px)",
        pb: { xs: 4, md: 5 },
        color: COLORS.muted,
      }}
    >
      <Box
        sx={{
          maxWidth: 1160,
          mx: "auto",
          mt: "28px",
          pt: "24px",
          borderTop: `1px solid ${COLORS.line}`,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: "18px",
          color: COLORS.muted,
        }}
      >
        {FOOTER_COLUMNS.map((col) => (
          <Box key={col.title}>
            <Box
              component="strong"
              sx={{
                color: COLORS.ink,
                fontFamily: "windroos, Georgia, serif",
                fontSize: "1.05rem",
                display: "block",
                mb: 0.75,
                lineHeight: 1.1,
              }}
            >
              {col.title}
            </Box>
            {col.body && (
              <Box
                component="p"
                sx={{
                  m: 0,
                  mb: col.links ? 1 : 0,
                  fontSize: "0.95rem",
                  lineHeight: 1.55,
                }}
              >
                {col.body}
              </Box>
            )}
            {col.links && (
              <Box
                component="ul"
                sx={{
                  listStyle: "none",
                  p: 0,
                  m: 0,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  fontSize: "0.95rem",
                }}
              >
                {col.links.map((link) => (
                  <li key={link.href}>
                    <Box
                      component="a"
                      href={link.href}
                      target={link.external ? "_blank" : undefined}
                      rel={link.external ? "noopener noreferrer" : undefined}
                      sx={{
                        color: COLORS.redInk,
                        textDecoration: "none",
                        fontWeight: 700,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {link.label}
                    </Box>
                  </li>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          maxWidth: 1160,
          mx: "auto",
          mt: 2,
          pt: 2,
          borderTop: `1px solid ${COLORS.line}`,
          fontSize: "0.82rem",
          color: COLORS.muted,
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 0.5, md: 1.5 },
          alignItems: "baseline",
        }}
      >
        <Box component="span">{getReleaseLabel()}</Box>
        <Box
          component="span"
          title={releaseInfo.commitShaFull || undefined}
          sx={{
            fontSize: "0.74rem",
            color: COLORS.muted,
          }}
        >
          {getReleaseMetaLine()}
        </Box>
      </Box>
    </Box>
  );
}

export default function VariantOnePublicShell({ children }) {
  return (
    <Box sx={{ backgroundColor: "#fff", color: COLORS.ink }}>
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "fixed",
          top: 8,
          left: 8,
          zIndex: 100,
          px: 2,
          py: 1.25,
          borderRadius: 1,
          backgroundColor: COLORS.ink,
          color: "#fff",
          fontFamily: "Poppins, sans-serif",
          fontWeight: 700,
          textDecoration: "none",
          transform: "translateY(calc(-100% - 16px))",
          transition: "transform 120ms ease",
          "&:focus": { transform: "translateY(0)" },
        }}
      >
        Naar hoofdinhoud
      </Box>
      <TopRibbon />
      <NavBar />
      <Box id="main-content" tabIndex={-1}>
        {children}
      </Box>
      <ShellFooter />
    </Box>
  );
}
